import {PowderguideApiTokenStorage} from "./powderguide-api-token-storage";

export interface UserData {
    id: string,
    username: string,
    name: string,
    is_admin: boolean,
    is_cr_pro: boolean,
}

export interface Comment {
    uuid: string,
    comment: string,
    created_at: string,
    updated_at: string,
    language: string,
    commentator: {
        username: string,
        name: string,
    },
    translations: Record<string, { comment: string }>,
}

export interface Spot {
    uuid: string
    title: string
    country: string
    location: {
        lat: number
        long: number
    }
    url?: string
}

export interface SpotForMap extends Spot {
    conditionReport: {
        uuid: string
        author: {
            username: string
        },
        activityDate: string
        title: string
        counter: number
        season: string
        rating: number
        snowCondition: {
            rating: number
        }
        snowQuality: {
            rating: number
        }
        mainImage: string,
    }
}

type PasswordResetStatus = 'success'|'error'|'invalid_token'|'token_expired'|'already_used'

export interface PowderguideApiInterface {
    currentLanguage(): string|null;
    login(username: string, password: string): Promise<UserData | null>;

    oauthExchange(code: string): Promise<UserData | null>;

    handoffExchange(code: string): Promise<UserData | null>;

    logout(): Promise<void>;

    getUser(): Promise<UserData | null>;

    loadSpotsForMap(activityAfter: string, activityBefore: string): Promise<SpotForMap[]>;

    loadLatestComments(limit: number): Promise<Comment[]>;

    loadComments(target: 'entry'|'conditions_report', uuid: string): Promise<Comment[]>;

    postComment(target: 'entry'|'conditions_report', uuid: string, comment: string): Promise<Comment>;

    deleteComment(uuid: string): Promise<void>;

    register(userProperties: RegistrationRequest): Promise<RegistrationResponse>;

    confirmRegistration(token: string): Promise<RegistrationConfirmationResponse>;

    resendRegistrationEmail(emailAddress: string): Promise<{ success: boolean }>;

    sendPasswordResetMail(emailAddress: string): Promise<SendPasswordResetMailResponse>;

    resetPassword(token: string, password: string, passwordConfirmation: string): Promise<ResetPasswordResponse>;

    fetchArticles(request: ArticleRequest): Promise<ArticleResponse>
    fetchEvents(request: EventRequest): Promise<EventResponse>
    fetchConditionsReports(request: ConditionsReportRequest): Promise<ConditionsReportResponse>
    fetchConditionsReportFilterMeta(): Promise<ConditionsReportFilterMetaInformation>
}

export interface SendPasswordResetMailResponse extends LaravelValidationError {
    success: boolean
}

export interface ResetPasswordResponse extends LaravelValidationError {
    success: boolean,
    status: PasswordResetStatus
}

export interface RegistrationRequest {
    username: string,
    password: string,
    password_confirmation: string,
    email_address: string,
    has_acknowledged_toc: boolean,
    wants_to_receive_newsletter: boolean,
    name?: string,
    address?: string,
    zip?: string,
    city?: string,
    country?: string,
    instagram_username?: string,
}

export interface LaravelValidationError {
    message: string,
    errors: {
        [property: string]: string[],
    }
}

export interface RegistrationResponse extends LaravelValidationError {
    success: boolean
}

export interface RegistrationConfirmationResponse {
    success: boolean,
    result: 'success'| 'error_invalid_token'| 'error_generic'| 'error_expired'| 'error_already_confirmed'| 'error_token_mismatch',
    user: UserData,
}

export interface PaginationInformation {
    page: number,
    next_page: number,
    previous_page: number|null,
    last_page: number|null,
    per_page: number,
    from_item: number,
    to_item: number,
    total_items: number,
}

export interface ArticleRequest {
    page: number,
    page_size: number,
    categories: string[],
    search: string,
}

export interface ArticleCategoryGroup {
    id: string,
    title: string,
    categories: ArticleCategory[],
}

export interface ArticleCategory {
    id: string,
    title: string,
}

export interface Article {
    id: string,
    title: string,
    teaser_text: string,
    url: string,
    teaser_image: string,
    category: string,
    date: string,
    date_german: string,
    authors: {
        id: string,
        name: string
    }[],
    authors_combined: string,
}

export interface ArticleResponse {
    pagination: PaginationInformation,
    data: Article[],
    categories: ArticleCategoryGroup[],
}

export interface EventRequest {
    page: number,
    page_size: number,
    categories: string[],
}


export interface Event{
    id: string,
    title: string,
    subtitle: string,
    teaser: string,
    image: string,
    start_date_time: string,
    start_date: string,
    start_date_time_formatted: string,
    end_date_time: string,
    end_date: string|null,
    end_date_time_formatted: string,
    categories: EventCategory[],
    url: string,
}

export interface EventMonth {
    month: string,
    month_label: string,
    events: Event[],
}

export interface EventCategory {
    id: string,
    title: string,
}

export interface EventResponse {
    pagination: PaginationInformation,
    data: EventMonth[],
    categories: EventCategory[],
}

export interface ConditionsReport {
    uuid: string,
    title: string,
    subline: string,
    type: 'pro'|'mini',
    language: string,
    activity_date: string,
    created_at: string,
    updated_at: string,
    media: string,
    media_thumbnails: {
        square: string|null,
        list_portrait: string|null,
        list_landscape: string|null,
    }|null,
    author: {
        uuid: string,
        username: string,
    }
    spot: {
        uuid: string,
        title: string
        country: string,
    },
    urls: {
        de: string,
        en: string,
        fr: string,
        it: string,
        es: string,
        cs: string,
        current: string,
        source: string,
    },
}

export interface ConditionsReportResponse {
    conditions_reports: ConditionsReport[],
    pagination: PaginationInformation,
}

export interface ConditionsReportRequest {
    ratings?: number[],
    spots?: string[],
    author?: string,
    older_than?: string,
    newer_than?: string,
    order_by?: string,
    order_direction?: 'ASC'|'DESC',
    type?: 'pro'|'mini',
    page?: number,
    per_page?: number,
}

export interface ConditionsReportSpot {
    uuid: string,
    title: string,
    country: string,
    location: {
        lat: number,
        long: number,
    }
    translations: {
        [language: string]: {
            title: string,
        }
    }
}

export interface ConditionsReportAuthor {
    uuid: string,
    username: string,
    name: string|null,
}

export interface ConditionsReportFilterMetaInformation {
    authors: ConditionsReportAuthor[],
    spots: ConditionsReportSpot[],
}

export class ApiError extends Error {
    constructor(message: string, public readonly status: number) {
        super(message);
        this.name = 'ApiError';
    }
}

export class PowderguideApi implements PowderguideApiInterface {
    constructor(
        private tokenStorage: PowderguideApiTokenStorage,
        private lang: string|null,
    ) {
    }

    public currentLanguage(): string | null {
        return this.lang;
    }

    public async register(userProperties: RegistrationRequest): Promise<RegistrationResponse> {
        return this.statamicRequest('api/v2/register', 'POST', userProperties);
    }

    public async confirmRegistration(token: string): Promise<RegistrationConfirmationResponse> {
        return this.statamicRequest('api/v2/register/confirm', 'POST', { token: token });
    }

    public async resendRegistrationEmail(emailAddress: string): Promise<{ success: boolean }> {
        return this.statamicRequest('api/v2/register/resend-confirmation',  'POST', { email_address: emailAddress });
    }

    loadLatestComments(limit: number): Promise<Comment[]> {
        return this.statamicRequest<Comment[]>(`api/v2/comment/latest?limit=${limit}`, 'GET');
    }

    loadSpotsForMap(activityAfter: string, activityBefore: string): Promise<SpotForMap[]> {
        return this.statamicRequest<SpotForMap[]>(
            `api/v2/spot/map?activity_after=${activityAfter}&activity_before=${activityBefore}`,
            'GET',
        )
    }

    /** Keep the token (Sanctum's "id|secret", the API wants the secret only) and load its user. */
    private signInWithToken(plainTextToken: string): Promise<UserData | null> {
        this.tokenStorage.store(plainTextToken.split('|')[1]);

        return this.getUser();
    }

    public async login(username: string, password: string): Promise<UserData | null> {
        const data = await this.statamicRequest<{ token: string }>('api/v2/authentication', 'POST', {
            username: username,
            password: password,
            device_id: 'website',
        })

        return this.signInWithToken(data.token);
    }

    public oauthExchange(code: string): Promise<UserData | null> {
        return this.exchangeCode('api/v2/oauth/exchange', code);
    }

    /** Sign in with the one-time code the mobile app opened this page with; replaces any token stored here. */
    public handoffExchange(code: string): Promise<UserData | null> {
        return this.exchangeCode('api/v2/oauth/handoff/exchange', code);
    }

    private async exchangeCode(path: string, code: string): Promise<UserData | null> {
        const data = await this.statamicRequest<{ token: string }>(path, 'POST', {
            code: code,
            device_id: 'website',
        })

        return this.signInWithToken(data.token);
    }

    public async logout(): Promise<void> {
        const token = this.tokenStorage.get();
        if (!token) {
            return;
        }

        await this.statamicRequest('api/v2/authentication', 'DELETE');
        this.tokenStorage.clear();
    }

    public async getUser(): Promise<UserData | null> {
        const token = this.tokenStorage.get();
        if (!token) {
            return Promise.resolve(null);
        }

        const response = await this.statamicRequest<{ user: UserData|null }>('api/v2/authentication', 'GET');

        return response.user;
    }

    loadComments(target: 'entry'|'conditions_report', uuid: string): Promise<Comment[]> {
        return this.statamicRequest<Comment[]>(`api/v2/${target}/${uuid}/comment`);
    }

    postComment(target: "entry" | "conditions_report", uuid: string, comment: string): Promise<Comment> {
        return this.statamicRequest<Comment>(`api/v2/${target}/${uuid}/comment`, 'POST', { comment: comment });
    }

    async deleteComment(uuid: string): Promise<void> {
        await this.statamicRequest(`api/v2/comment/${uuid}`, 'DELETE');
    }

    async statamicRequest<TResponse>(url: string, method: string = 'GET', body: any = null): Promise<TResponse> {
        const token = this.tokenStorage.get();

        const options: RequestInit = {
            mode: 'cors',
            credentials: 'include',
            method: method,
            headers: {
                Accept: 'application/json'
            }
        }

        if (token) {
            options.headers['Authorization'] = `Bearer ${token}`;
        }

        if (body) {
            options.headers['Content-Type'] = 'application/json'
            options.body = JSON.stringify(body)
        }

        if (this.lang) {
            options.headers['Accept-Language'] = this.lang;
        }

        const response = await fetch(`/${url}`, options)
        const json = await response.json();

        if (response.status >= 500 || response.status === 429 || json.error) {
            const errorMessage = json.error || json.message || 'An unknown error occurred';
            throw new ApiError(`API request failed: ${errorMessage}`, response.status);
        }

        return json;
    }

    async sendPasswordResetMail(emailAddress: string): Promise<SendPasswordResetMailResponse> {
        return await this.statamicRequest('api/v2/password-reset/send', 'POST', {
            email_address: emailAddress,
        });
    }

    async resetPassword(token: string, password: string, passwordConfirmation: string): Promise<ResetPasswordResponse> {
        return await this.statamicRequest('api/v2/password-reset/reset', 'POST', {
            token: token,
            password: password,
            password_confirmation: passwordConfirmation,
        })
    }

    async fetchArticles(request: ArticleRequest): Promise<ArticleResponse> {
        return await this.statamicRequest(`api/v2/article?${this.objectToQueryParams(request)}`, 'GET')
    }

    async fetchEvents(request: EventRequest): Promise<EventResponse> {
        return await this.statamicRequest(`api/v2/event?${this.objectToQueryParams(request)}`, 'GET')
    }

    async fetchConditionsReports(request: ConditionsReportRequest): Promise<ConditionsReportResponse> {
        return await this.statamicRequest(`api/v2/conditions_report?${this.objectToQueryParams(request)}`, 'GET')
    }

    async fetchConditionsReportFilterMeta(): Promise<ConditionsReportFilterMetaInformation> {
        return await this.statamicRequest('api/v2/conditions_report/meta', 'GET')
    }

    private objectToQueryParams(obj: Record<string, any>): string {
        const params = new URLSearchParams();
        for (const [key, value] of Object.entries(obj)) {
            if (!value) {
                continue;
            }
            if (Array.isArray(value)) {
                for (const [ i,item ] of Object.entries(value)) {
                    params.append(`${key}[${i}]`, item);
                }
            } else {
                params.append(key, value);
            }
        }

        return params.toString();
    }
}

export class PowderguideApiCacheDecorator implements PowderguideApiInterface {

    private cache: {
        cacheBust: string,
        contents: {
            [name: string]: { expires: number, data: any }
        }
    } | null = null;

    constructor(
        private api: PowderguideApiInterface,
        private tokenStorage: PowderguideApiTokenStorage,
        private cacheLifetimeSeconds: number,
        private cacheBust: string
    ) {
    }

    currentLanguage(): string | null {
        return this.api.currentLanguage();
    }

    register(userProperties: RegistrationRequest): Promise<RegistrationResponse> {
        return this.api.register(userProperties);
    }


    confirmRegistration(token: string): Promise<RegistrationConfirmationResponse> {
        return this.api.confirmRegistration(token);
    }

    resendRegistrationEmail(emailAddress: string): Promise<{ success: boolean }> {
        return this.api.resendRegistrationEmail(emailAddress);
    }

    async getUser(): Promise<UserData | null> {
        const token = this.tokenStorage.get();
        if (token) {
            const userFromCache = this.getFromCache<UserData>(`user-${token}`);
            if (userFromCache) {
                return Promise.resolve(userFromCache);
            }
        }

        const user = await this.api.getUser();
        if (user) {
            this.setCache(`user-${token}`, user);
        }

        return user;
    }

    async loadLatestComments(limit: number): Promise<Comment[]> {
        const cached = this.getFromCache<Comment[]>(`latest_comments_${limit}`)
        if (cached) {
            return cached
        }

        const data = await this.api.loadLatestComments(limit)
        this.setCache(`latest_comments_${limit}`, data)

        return data
    }

    async loadSpotsForMap(activityAfter: string, activityBefore: string): Promise<SpotForMap[]> {
        const cacheKey = `spots_for_map_${activityAfter}_${activityBefore}`;
        const cached = this.getFromCache<SpotForMap[]>(cacheKey)
        if (cached) {
            return cached;
        }

        const data = await this.api.loadSpotsForMap(activityAfter, activityBefore);
        this.setCache(cacheKey, data);

        return data;
    }

    async fetchArticles(request: ArticleRequest): Promise<ArticleResponse> {
        const cacheKey = `articles_${JSON.stringify(request)}`;
        const cached = this.getFromCache<ArticleResponse>(cacheKey);
        if (cached) {
            return Promise.resolve(cached);
        }

        const data = await this.api.fetchArticles(request);
        this.setCache(cacheKey, data, 60);

        return data;
    }

    async fetchEvents(request: EventRequest): Promise<EventResponse> {
        const cacheKey = `events_${JSON.stringify(request)}`;
        const cached = this.getFromCache<EventResponse>(cacheKey);
        if (cached) {
            return Promise.resolve(cached);
        }

        const data = await this.api.fetchEvents(request);
        this.setCache(cacheKey, data, 60);

        return data;
    }

    async fetchConditionsReports(request: ConditionsReportRequest): Promise<ConditionsReportResponse> {
        const cacheKey = `crs_${JSON.stringify(request)}`;
        const cached = this.getFromCache<ConditionsReportResponse>(cacheKey);
        if (cached) {
            return Promise.resolve(cached);
        }

        const data = await this.api.fetchConditionsReports(request);
        this.setCache(cacheKey, data, 60);

        return data;
    }

    async fetchConditionsReportFilterMeta(): Promise<ConditionsReportFilterMetaInformation> {
        const cacheKey = `cr_filter_meta`;
        const cached = this.getFromCache<ConditionsReportFilterMetaInformation>(cacheKey);
        if (cached) {
            return Promise.resolve(cached);
        }

        const data = await this.api.fetchConditionsReportFilterMeta();
        this.setCache(cacheKey, data, 300);

        return data;
    }

    loadComments(target: "entry" | "conditions_report", uuid: string): Promise<Comment[]> {
        return this.api.loadComments(target, uuid);
    }

    postComment(target: "entry" | "conditions_report", uuid: string, comment: string): Promise<Comment> {
        return this.api.postComment(target, uuid, comment);
    }

    deleteComment(uuid: string): Promise<void> {
        return this.api.deleteComment(uuid);
    }

    login(username: string, password: string): Promise<UserData | null> {
        return this.api.login(username, password);
    }

    oauthExchange(code: string): Promise<UserData | null> {
        return this.api.oauthExchange(code);
    }

    handoffExchange(code: string): Promise<UserData | null> {
        return this.api.handoffExchange(code);
    }

    logout(): Promise<void> {
        const token = this.tokenStorage.get();
        if (token) {
            this.setCache(`user-${token}`, null);
        }

        return this.api.logout();
    }

    resetPassword(token: string, password: string, passwordConfirmation: string): Promise<ResetPasswordResponse> {
        return this.api.resetPassword(token, password, passwordConfirmation);
    }

    sendPasswordResetMail(emailAddress: string): Promise<SendPasswordResetMailResponse> {
        return this.api.sendPasswordResetMail(emailAddress);
    }

    private getFromCache<T>(name: string): T {
        name = `${this.api.currentLanguage()}_${name}`
        if (this.cache === null) {
            this.cache = JSON.parse(localStorage.getItem('powderguide-api-cache') || '{}');
        }
        if (this.cache.cacheBust !== this.cacheBust) {
            this.cache = {
                cacheBust: this.cacheBust,
                contents: {},
            };
            this.setCache(name, null);
        }

        const data = this.cache && this.cache.contents && this.cache.contents[name] ? this.cache.contents[name] : false;
        if (!data) {
            return null;
        }

        if (data.expires < Date.now()) {
            this.setCache(name, null);
            return null;
        }

        return JSON.parse(JSON.stringify(data.data));
    }

    private setCache<T>(name: string, data: T, cacheLifetimeSeconds: number = this.cacheLifetimeSeconds): void {
        name = `${this.api.currentLanguage()}_${name}`
        this.initializeCache();
        if (data === null) {
            delete this.cache.contents[name];
        } else {
            this.cache.contents[name] = {
                expires: Date.now() + cacheLifetimeSeconds * 1000,
                data,
            };
        }

        localStorage.setItem('powderguide-api-cache', JSON.stringify(this.cache));
    }

    private initializeCache(): void {
        if (this.cache === null) {
            this.cache = JSON.parse(localStorage.getItem('powderguide-api-cache') || '{}');
        }
        if (this.cache.cacheBust !== this.cacheBust) {
            this.cache = {
                cacheBust: this.cacheBust,
                contents: {},
            };
            this.setCache('', null);
        }
    }
}
